import { ContextSelectionExpression } from "./contextSelection.ts";
import * as Schema from "effect/Schema";

import { EnvironmentId, NonNegativeInt, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";

/** Product role of a Neucleos Door. This is not a build target or implementation name. */
export const NeucleosDoorRole = Schema.Literals(["desk", "web", "pocket", "watch", "ambient"]);
export type NeucleosDoorRole = typeof NeucleosDoorRole.Type;

/**
 * Provider harness selected behind a Door. This is not Door or transport
 * identity — and it is NOT the provider driver kind. Two registers carry the
 * word "claude" and they are not interchangeable:
 *
 *   driver kind : "claudeAgent" | "codex" | "aiSdk" | …
 *                 (open slug; what an adapter reports as its provider)
 *   harness     : this closed set; the key a World's MCP grant is filed under
 *
 * Crossing them yields an unsatisfiable predicate: a Claude thread's driver
 * kind never equals the harness name "claude". Use
 * `neucleosHarnessForDriverKind` to move between the registers; never compare
 * a driver kind against a harness name directly.
 *
 * Each supported engine receives its own World grant through sign-in.
 * Grant eligibility does not select tools or content for a message; the
 * person's selection is still frozen separately at Send.
 */
export const NeucleosProviderHarness = Schema.Literals([
  "claude",
  "codex",
  // "cursor" left this register 2026-09-18 (the principal: "lets just remove
  // cursor as a provider"); "grok", "opencode" and "antigravity" left it
  // 2026-10-17 (the principal: "Let's remove Antigravity, Grok, and OpenCode").
  // The World keeps those literals only for stored rows.
  "aiSdk",
]);
export type NeucleosProviderHarness = typeof NeucleosProviderHarness.Type;

/**
 * The one crossing between the driver-kind register and the harness register.
 *
 * `null` means the driver runs no governed World work: it has no harness, so
 * there is no grant to record a frozen boundary through. Callers refuse on
 * `null` rather than proceed — an unrecordable boundary cannot be enforced,
 * and a decorative rail is worse than a refusal (O19).
 */
export function neucleosHarnessForDriverKind(driverKind: string): NeucleosProviderHarness | null {
  switch (driverKind) {
    // The Claude adapter reports "claudeAgent" and files its grant under
    // "claude". Both names are correct, each in its own register.
    case "claudeAgent":
      return "claude";
    case "codex":
      return "codex";
    // D12 (2026-08-29): the governed set extends to every engine a seat can
    // be born on. This lane files grants under its own key — the same
    // spelling lands in both registers, but they remain two registers.
    case "aiSdk":
      return "aiSdk";
    default:
      return null;
  }
}

export const NeucleosDoorInstanceId = TrimmedNonEmptyString.pipe(
  Schema.brand("NeucleosDoorInstanceId"),
);
export type NeucleosDoorInstanceId = typeof NeucleosDoorInstanceId.Type;

export const NeucleosWorldKey = TrimmedNonEmptyString.pipe(Schema.brand("NeucleosWorldKey"));
export type NeucleosWorldKey = typeof NeucleosWorldKey.Type;

export const NeucleosWorldActorId = TrimmedNonEmptyString.pipe(
  Schema.brand("NeucleosWorldActorId"),
);
export type NeucleosWorldActorId = typeof NeucleosWorldActorId.Type;

export const NeucleosWorldSessionId = TrimmedNonEmptyString.pipe(
  Schema.brand("NeucleosWorldSessionId"),
);
export type NeucleosWorldSessionId = typeof NeucleosWorldSessionId.Type;

export const NeucleosProviderThreadId = TrimmedNonEmptyString.pipe(
  Schema.brand("NeucleosProviderThreadId"),
);
export type NeucleosProviderThreadId = typeof NeucleosProviderThreadId.Type;

/**
 * Coordinates a Door already attached to one durable World Session and one
 * Cleo thread on an addressed Desk. Actor and provider continuity are omitted:
 * only the owning Desk may resolve those authoritative values.
 */
export const NeucleosDoorSessionAttachRequest = Schema.Struct({
  schemaVersion: Schema.Literal("neucleos-door-session-attach-v1"),
  doorRole: NeucleosDoorRole,
  doorInstanceId: NeucleosDoorInstanceId,
  worldKey: NeucleosWorldKey,
  worldSessionId: NeucleosWorldSessionId,
  addressedDeskEnvironmentId: EnvironmentId,
  threadId: ThreadId,
});
export type NeucleosDoorSessionAttachRequest = typeof NeucleosDoorSessionAttachRequest.Type;

/** The authoritative, read-back binding returned by the addressed Desk. */
export const NeucleosDoorSessionBinding = Schema.Struct({
  ...NeucleosDoorSessionAttachRequest.fields,
  actorId: NeucleosWorldActorId,
  providerThreadId: Schema.NullOr(NeucleosProviderThreadId),
});
export type NeucleosDoorSessionBinding = typeof NeucleosDoorSessionBinding.Type;

/** Names-only lookup of existing executions in one explicitly selected session. */
export const NeucleosDoorSessionDiscoveryRequest = Schema.Struct({
  schemaVersion: Schema.Literal("neucleos-door-session-discovery-v1"),
  worldKey: NeucleosWorldKey,
  worldSessionId: NeucleosWorldSessionId,
  addressedDeskEnvironmentId: EnvironmentId,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type NeucleosDoorSessionDiscoveryRequest = typeof NeucleosDoorSessionDiscoveryRequest.Type;

export const NeucleosDoorSessionDiscovery = Schema.Struct({
  ...NeucleosDoorSessionDiscoveryRequest.fields,
  actorId: NeucleosWorldActorId,
  threads: Schema.Array(Schema.Struct({ threadId: ThreadId, title: Schema.String })),
});
export type NeucleosDoorSessionDiscovery = typeof NeucleosDoorSessionDiscovery.Type;

/** Current selected Context receipt for this connection's exact bound execution. */
export const NeucleosDoorContextSelection = Schema.Struct({
  // Arc SEL (BD-CAN-13): the stored set receipt. The legacy catalog-bound
  // receipt stays decodable; catalog digests are no longer part of identity.
  schemaVersion: Schema.Literals(["world-context-selection-v1", "world-session-selection-v1"]),
  selectionCheckpoint: Schema.optional(Schema.Literal(true)),
  worldKey: TrimmedNonEmptyString,
  roomId: TrimmedNonEmptyString,
  sessionId: TrimmedNonEmptyString,
  agentEnvironmentId: TrimmedNonEmptyString,
  agentThreadId: TrimmedNonEmptyString,
  catalogDigest: Schema.optional(Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u))),
  catalogCustodyDigest: Schema.optional(Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u))),
  selectionCut: NonNegativeInt,
  selectionDigest: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u)),
  memberIds: Schema.Array(TrimmedNonEmptyString),
  expressions: Schema.optional(Schema.Array(ContextSelectionExpression)),
  workingContextBitmap: Schema.optional(TrimmedNonEmptyString),
  memberManifest: Schema.optional(
    Schema.Struct({
      manifestId: TrimmedNonEmptyString,
      manifestDigest: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u)),
      itemCount: NonNegativeInt,
    }),
  ),
});
export type NeucleosDoorContextSelection = typeof NeucleosDoorContextSelection.Type;

/** The bound connection supplies identity; the client names its message and displayed selection. */
export const NeucleosDoorPrepareContextSelectionRequest = Schema.Struct({
  /** The host retains this message's exact boundary for ordinary Send and retry. */
  messageId: TrimmedNonEmptyString,
  expectedSelectionCut: NonNegativeInt,
  expectedSelectionDigest: Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u)),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type NeucleosDoorPrepareContextSelectionRequest =
  typeof NeucleosDoorPrepareContextSelectionRequest.Type;

/** The socket binding supplies Actor/World/session; clients may change only choices. */
export const NeucleosDoorSaveSessionToolsRequest = Schema.Struct({
  expectedRevision: NonNegativeInt,
  toolNames: Schema.NullOr(
    // SESSION_TOOL_NAMES_MAX in @cleo/shared (contracts cannot import shared).
    Schema.Array(TrimmedNonEmptyString.check(Schema.isMaxLength(256))).check(
      Schema.isMaxLength(1024),
    ),
  ),
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type NeucleosDoorSaveSessionToolsRequest = typeof NeucleosDoorSaveSessionToolsRequest.Type;

/** Machine-readable refusal reasons shared by attach and bound ordinary RPCs. */
export const NeucleosDoorSessionErrorReason = Schema.Literals([
  "device-session-renewal-required",
  "door-unbound",
  "world-credential-required",
  "world-credential-invalid",
  "world-actor-mismatch",
  "world-membership-required",
  "device-session-inactive",
  "door-operation-unavailable",
  "tool-selection-unavailable",
  "context-selection-unavailable",
  "context-selection-changed",
  "world-grant-unavailable",
  "world-session-unverified",
  "world-unreachable",
  "desk-environment-mismatch",
  "thread-not-found",
  "thread-coordinate-mismatch",
  "connection-already-bound",
  "thread-binding-mismatch",
  "projection-unavailable",
  "web-door-unbound",
]);
export type NeucleosDoorSessionErrorReason = typeof NeucleosDoorSessionErrorReason.Type;

/**
 * A value-safe Door refusal. It deliberately carries no grant, token, or
 * provider credential material.
 */
export class NeucleosDoorSessionError extends Schema.TaggedErrorClass<NeucleosDoorSessionError>()(
  "NeucleosDoorSessionError",
  {
    reason: NeucleosDoorSessionErrorReason,
    message: TrimmedNonEmptyString,
  },
) {}

/**
 * One Live door operation carried over the bound Pocket connection. The Desk's
 * HTTP Live routes are loopback-only; this RPC is the paired Pocket's
 * authenticated path to the same World admission core. World identity comes
 * only from the connection's binding; the client names the operation, the
 * conversation it displays and the bounded operation arguments.
 */
export const NeucleosDoorLiveVoiceOperation = Schema.Literals([
  "open",
  "quote",
  "presentation",
  "start",
  "extend",
  "read",
  "stop",
  "privacy",
  "privacy-set",
  "voice-set",
]);
export type NeucleosDoorLiveVoiceOperation = typeof NeucleosDoorLiveVoiceOperation.Type;

const LiveVoiceCoordinate = TrimmedNonEmptyString.check(
  Schema.isMaxLength(128),
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u),
);
export const NeucleosDoorLiveVoiceConversation = Schema.Struct({
  roomId: LiveVoiceCoordinate,
  sessionId: LiveVoiceCoordinate,
  environmentId: LiveVoiceCoordinate,
  threadId: LiveVoiceCoordinate,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type NeucleosDoorLiveVoiceConversation = typeof NeucleosDoorLiveVoiceConversation.Type;

export const NEUCLEOS_DOOR_LIVE_VOICE_ARGS_MAX_BYTES = 128 * 1024;
/** Operation arguments are opaque here and validated by the Live core; only their bound is fixed. */
export const NeucleosDoorLiveVoiceArgs = Schema.Record(
  Schema.String.check(Schema.isMaxLength(64)),
  Schema.Unknown,
).check(
  Schema.makeFilter(
    (args) =>
      (Object.keys(args).length <= 32 &&
        JSON.stringify(args).length <= NEUCLEOS_DOOR_LIVE_VOICE_ARGS_MAX_BYTES) ||
      "Live arguments exceed the bounded record.",
  ),
);
export type NeucleosDoorLiveVoiceArgs = typeof NeucleosDoorLiveVoiceArgs.Type;

export const NeucleosDoorLiveVoiceRequest = Schema.Struct({
  operation: NeucleosDoorLiveVoiceOperation,
  conversation: NeucleosDoorLiveVoiceConversation,
  args: NeucleosDoorLiveVoiceArgs,
}).annotate({ parseOptions: { onExcessProperty: "error" } });
export type NeucleosDoorLiveVoiceRequest = typeof NeucleosDoorLiveVoiceRequest.Type;

/**
 * The World's value-safe reply as a record: `{ ok: true, ...value }` for an
 * accepted operation, or `{ ok: false, kind, reason, admission? }` for a
 * refusal the client must display without a hidden fallback. Door-level
 * refusals (unbound, foreign conversation) fail the RPC instead.
 */
export const NeucleosDoorLiveVoiceResult = Schema.Record(Schema.String, Schema.Unknown);
export type NeucleosDoorLiveVoiceResult = typeof NeucleosDoorLiveVoiceResult.Type;
